import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router";

import { useMediaUrl } from "../api/hooks.ts";
import { Icon } from "./Icon.tsx";

/**
 * El visor grande de la plantilla (`.lightbox`), compartido por el detalle de la
 * operacion y por la Biblioteca.
 *
 * Aqui SI cierra un clic fuera, al reves que `Modal`. La regla de `Modal` existe
 * porque detras hay un formulario largo y un roce del pulgar tiraria diez campos
 * escritos; en un visor no hay nada que perder, y salir tocando el fondo negro
 * es el gesto que todo el mundo intenta primero.
 *
 * Ensena la variante `view` (1920 px) porque es la unica pantalla donde se mira
 * el grafico de verdad. La rejilla y las miniaturas usan `thumb`.
 */

export type LightboxShot = {
  contentHash: string;
  /** Los de la variante `view`, que es la que se pinta aqui. */
  width: number;
  height: number;
  title: string;
  meta: string;
  /**
   * LO QUE ESCRIBISTE SOBRE LA IMAGEN, o nulo si no hay nada escrito.
   *
   * Lo pidio el propietario senalando el hueco con una flecha: "aqui deberia
   * estar la nota de la imagen para poder leerla, que este en color blanco bien
   * legible". Tenia razon en el fondo: una pieza de Social Data se guarda POR lo
   * que dice la nota -- es la lectura que hiciste del grafico --, y el visor es
   * donde se relee.
   *
   * Es obligatorio y anulable, no opcional: asi cada sitio que abre el visor
   * decide. Las capturas de una operacion pasan `null` a proposito; su pie de
   * foto ya viaja en `title`, y repetirlo abajo seria leerlo dos veces.
   */
  note: string | null;
  /**
   * El resultado, o NULO cuando la pieza no tiene ninguno: el material de Social
   * Data no gano ni perdio nada, y en este producto la insignia significa
   * resultado (ADR 0003). Con nulo no se pinta, en vez de inventarse un rotulo.
   */
  badgeText: string | null;
  /** `win`, `loss` o `be`, el mismo vocabulario que `badgeOf`. */
  badgeTone: string;
  /** A donde lleva "Ver la operacion", o `null` si ya estas en ella. */
  tradeId: string | null;
};

export function Lightbox(props: {
  shots: readonly LightboxShot[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}): ReactNode {
  const mediaUrl = useMediaUrl();
  const close = useRef<HTMLButtonElement>(null);
  const { shots, index, onIndex, onClose } = props;
  const shot = shots[index];
  const many = shots.length > 1;

  useEffect(() => {
    const restore = document.activeElement;

    document.body.style.overflow = "hidden";
    close.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();

        return;
      }

      /* Las flechas son lo que se espera de un visor, y en la Biblioteca es la
         diferencia entre repasar treinta capturas y abrir y cerrar treinta
         veces. Sin mas de una, no hacen nada. */
      if (shots.length < 2) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onIndex((index - 1 + shots.length) % shots.length);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onIndex((index + 1) % shots.length);
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";

      if (restore instanceof HTMLElement) restore.focus();
    };
  }, [index, onClose, onIndex, shots.length]);

  if (shot === undefined) return null;

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={shot.title}
      /* Solo el fondo cierra, no lo que hay encima: sin esta comprobacion, un
         clic en la propia imagen cerraria el visor. */
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        className="lb-close"
        ref={close}
        aria-label="Cerrar el visor"
        onClick={onClose}
      >
        <Icon name="x" />
      </button>

      {many && (
        <button
          type="button"
          className="lb-nav prev"
          aria-label="Captura anterior"
          onClick={() => onIndex((index - 1 + shots.length) % shots.length)}
        >
          <Icon name="prev" />
        </button>
      )}

      <div className="lb-inner">
        <img
          src={mediaUrl(shot.contentHash, "view")}
          width={shot.width}
          height={shot.height}
          alt={shot.title}
        />

        {/* DEBAJO DE LA IMAGEN Y ENCIMA DEL PIE. Encima del pie y no dentro: el
            pie es una linea de datos -- titulo, fuente, fecha -- y la nota es un
            parrafo que se lee. Metida ahi quedaria recortada por el mismo
            `nowrap` que mantiene la linea en su sitio. */}
        {shot.note !== null && shot.note !== "" && <p className="lb-note">{shot.note}</p>}

        <div className="lb-cap">
          <span>
            <b>{shot.title}</b> <span className="muted">{shot.meta}</span>
            {many && <span className="muted"> · {index + 1} de {shots.length}</span>}
          </span>
          <span className="row">
            {shot.badgeText !== null && (
              <span className={`badge ${shot.badgeTone}`}>{shot.badgeText}</span>
            )}
            {shot.tradeId !== null && (
              <Link className="btn ghost sm" to={`/trades/${shot.tradeId}`} onClick={onClose}>
                <Icon name="feed" />
                Ver la operacion
              </Link>
            )}
          </span>
        </div>
      </div>

      {many && (
        <button
          type="button"
          className="lb-nav next"
          aria-label="Captura siguiente"
          onClick={() => onIndex((index + 1) % shots.length)}
        >
          <Icon name="next" />
        </button>
      )}
    </div>
  );
}
